import { ClockCircleOutlined, WarningOutlined } from '@ant-design/icons';
import { Avatar, Tag, Tooltip, Typography } from 'antd';

import type { JobFlight, Segment } from '../../../src/types.ts';
import { fmtMinutes, splitLegs, type Leg } from '../lib/itinerary.ts';
import { fmtDay, fmtTime } from '../lib/format.ts';

interface Props {
  flight: JobFlight;
  /** Hide the per-segment class/bag chips (for tight spaces). */
  compact?: boolean;
}

const LONG_LAYOVER_MIN = 6 * 60;

function bagsLabel(s: Segment): string {
  if (s.bags?.length) {
    return s.bags.map((b) => `${b.pieces ?? '?'}× ${b.type === 'CARRY_ON' ? 'carry-on' : 'checked'}`).join(', ');
  }
  return s.bagsText ?? '';
}

function SegmentRow({ s, compact }: { s: Segment; compact: boolean }) {
  const chips = [
    s.cabinClass ? s.cabinClass.replace('_', ' ').toLowerCase() : '',
    s.ticketClass ? `class ${s.ticketClass}` : '',
    bagsLabel(s),
  ].filter(Boolean);
  return (
    <div className="seg">
      <div className="seg__airline">
        <Avatar size={28} shape="square" src={s.airlineLogo ?? undefined}>
          {(s.airlineCode ?? s.airline ?? '?').slice(0, 2)}
        </Avatar>
        <div className="seg__airline-text">
          <span className="seg__name">{s.airline ?? 'Unknown airline'}</span>
          <span className="seg__no">{s.flightNo ?? ''}</span>
        </div>
      </div>
      <div className="seg__times">
        <div className="seg__point">
          <span className="seg__time">{fmtTime(s.dep) || '—'}</span>
          <span className="seg__code">{s.from ?? '?'}</span>
          <span className="seg__city">{s.fromCity || s.fromName || ''}</span>
          <span className="seg__date">{fmtDay(s.dep)}</span>
        </div>
        <div className="seg__arrow">
          <span className="seg__dur">{typeof s.duration === 'string' ? s.duration.replace(/\s+/g, ' ') : ''}</span>
          <div className="seg__line" />
        </div>
        <div className="seg__point seg__point--end">
          <span className="seg__time">{fmtTime(s.arr) || '—'}</span>
          <span className="seg__code">{s.to ?? '?'}</span>
          <span className="seg__city">{s.toCity || s.toName || ''}</span>
          <span className="seg__date">{fmtDay(s.arr)}</span>
        </div>
      </div>
      {!compact && (chips.length > 0 || s.seatsLeft != null || s.fareInfo) && (
        <div className="seg__chips">
          {chips.map((c) => (
            <Tag key={c} bordered={false}>
              {c}
            </Tag>
          ))}
          {s.seatsLeft != null && s.seatsLeft <= 5 && (
            <Tag color={s.seatsLeft <= 2 ? 'red' : 'orange'} bordered={false}>
              {s.seatsLeft} seat{s.seatsLeft === 1 ? '' : 's'} left
            </Tag>
          )}
          {s.fareInfo && (
            <Tooltip title={s.fareInfo}>
              <Tag bordered={false} color="geekblue" className="seg__fare">
                {s.fareInfo.length > 48 ? `${s.fareInfo.slice(0, 48)}…` : s.fareInfo}
              </Tag>
            </Tooltip>
          )}
        </div>
      )}
    </div>
  );
}

function LegBlock({ leg, compact }: { leg: Leg; compact: boolean }) {
  return (
    <div className="leg">
      <div className="leg__head">
        <Typography.Text strong>
          {leg.kind === 'out' ? 'Outbound' : 'Return'} · {leg.from} → {leg.to}
        </Typography.Text>
        <span className="leg__meta">
          {fmtDay(leg.departs)}
          {leg.totalMinutes != null ? ` · ${fmtMinutes(leg.totalMinutes)} total` : ''}
          {' · '}
          {leg.stops === 0 ? 'direct' : `${leg.stops} stop${leg.stops > 1 ? 's' : ''}`}
        </span>
      </div>
      {leg.segments.map((s, i) => {
        const lay = leg.layoverMinutes[i];
        return (
          <div key={`${s.flightNo ?? i}-${i}`}>
            {lay != null && (
              <div className={`layover${lay >= LONG_LAYOVER_MIN ? ' layover--long' : ''}`}>
                {lay >= LONG_LAYOVER_MIN ? <WarningOutlined /> : <ClockCircleOutlined />}
                <span>
                  {fmtMinutes(lay)} layover in {s.from ?? '?'}
                  {s.fromCity ? ` (${s.fromCity})` : ''}
                  {lay >= LONG_LAYOVER_MIN ? ' — long connection' : ''}
                </span>
              </div>
            )}
            <SegmentRow s={s} compact={compact} />
          </div>
        );
      })}
    </div>
  );
}

/** Every leg, every segment, every layover of a fare. */
export function FlightItinerary({ flight, compact = false }: Props) {
  const legs = splitLegs(flight);
  if (!legs.length) return <Typography.Text type="secondary">No segment details from this provider.</Typography.Text>;
  return (
    <div className="itinerary">
      {legs.map((leg) => (
        <LegBlock key={leg.kind} leg={leg} compact={compact} />
      ))}
      <div className="itinerary__foot">
        {flight.refundable != null && (
          <Tooltip title="As reported by the provider — fare rules decide the penalties.">
            <Tag color={flight.refundable ? 'green' : 'volcano'} bordered={false}>
              {flight.refundable ? 'Refundable' : 'Non-refundable'}
            </Tag>
          </Tooltip>
        )}
        <Tag bordered={false}>via {flight.provider}</Tag>
        {flight.gdsType && flight.gdsType !== flight.provider && <Tag bordered={false}>{flight.gdsType}</Tag>}
      </div>
    </div>
  );
}
